import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { drivelabels_v2 } from '@googleapis/drivelabels';
import { getAllDriveLabelsForManagerQuery } from '../../../api/users-api';
import { driveLabelsWriteAccessQuery } from '../../../api/drive-labels-api';
import SharedToolbar from '../../../shared/shared-toolbar-latest';
import SharedGridStateIconButton from '../../../shared/shared-grid-state-icon-button-latest';
import SharedButton from '../../../shared/buttons/shared-button';
import { ModuleList } from '../../../shared/analytics/module-list';
import SharedIconButton from '../../../shared/buttons/shared-icon-button';
import { pagesConfig } from '../../../shared/pages-config';
import { useUserSettings } from '../../../utils/use-user-settings';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import { Messages } from '../../../utils/message-constants';
import { getLabelToolbarActions } from './label-toolbar-actions';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import Tooltip from '@mui/material/Tooltip';
import Add from '@mui/icons-material/Add';

export function CreateLabelButton() {
  const navigate = useNavigate();
  const { driveRoles } = useUserSettings();
  const canCreate = isAbleToSeeUIElement(
    driveRoles,
    DriveRoles.CreateDriveLabels.value
  );
  const { data: hasWriteAccess, isLoading } = useQuery({
    ...driveLabelsWriteAccessQuery(),
    enabled: canCreate,
  });

  const disabledReason = !canCreate
    ? `You need the "${DriveRoles.CreateDriveLabels.name}" permission to create labels.`
    : !isLoading && !hasWriteAccess
    ? Messages.DriveLabels.NoWriteAccess
    : '';

  return (
    <Tooltip title={disabledReason}>
      <span>
        <SharedButton
          variant="primary"
          startIcon={<Add />}
          disabled={!!disabledReason || isLoading}
          onClick={() => navigate(`/${pagesConfig.createDriveLabel.link}`)}
        >
          Create Label
        </SharedButton>
      </span>
    </Tooltip>
  );
}

type Label = drivelabels_v2.Schema$GoogleAppsDriveLabelsV2Label;

/**
 * Role and scope checks plus the status and lock rules for the selected labels. Every toolbar
 * button reads the same cached scope answer, so this isn't one request per button.
 */
function useLabelToolbarActions(
  selectedLabels: Label[],
  hasActiveLockByLabelId: Record<string, boolean>,
  isDeleting = false
) {
  const { driveRoles } = useUserSettings();
  // Disable and Enable are one permission, like Enable/Disable Drive Sweep.
  const canDisable = isAbleToSeeUIElement(
    driveRoles,
    DriveRoles.UpdateDriveLabelsStatus.value
  );
  const canEnable = canDisable;
  const canDelete = isAbleToSeeUIElement(
    driveRoles,
    DriveRoles.DeleteDriveLabels.value
  );
  const { data: hasWriteAccess, isLoading } = useQuery({
    ...driveLabelsWriteAccessQuery(),
    enabled: canEnable || canDisable || canDelete,
  });

  return getLabelToolbarActions(selectedLabels, {
    hasActiveLockByLabelId,
    canEnable,
    canDisable,
    canDelete,
    isDeleting,
    hasWriteAccess: isLoading ? undefined : !!hasWriteAccess,
  });
}

interface DeleteLabelsButtonProps {
  selectedLabels: Label[];
  onDelete: () => void;
  /** A delete is running; a second one would hit labels that are already gone. */
  isDeleting?: boolean;
}

export function DeleteLabelsButton({
  selectedLabels,
  onDelete,
  isDeleting = false,
}: DeleteLabelsButtonProps) {
  const { delete: action } = useLabelToolbarActions(
    selectedLabels,
    {},
    isDeleting
  );
  if (!action.visible) {
    return null;
  }

  return (
    <SharedIconButton
      variant="deleteFilled"
      onClick={onDelete}
      disabled={action.disabled}
      tooltipLabel={action.hint ?? action.label}
    />
  );
}

interface DisableLabelsButtonProps {
  selectedLabels: Label[];
  hasActiveLockByLabelId: Record<string, boolean>;
  onDisable: () => void;
}

export function DisableLabelsButton({
  selectedLabels,
  hasActiveLockByLabelId,
  onDisable,
}: DisableLabelsButtonProps) {
  const { disable: action } = useLabelToolbarActions(
    selectedLabels,
    hasActiveLockByLabelId
  );
  if (!action.visible) {
    return null;
  }

  return (
    <SharedIconButton
      variant="close"
      onClick={onDisable}
      disabled={action.disabled}
      tooltipLabel={action.hint ?? action.label}
    />
  );
}

interface EnableLabelsButtonProps {
  selectedLabels: Label[];
  onEnable: () => void;
}

export function EnableLabelsButton({
  selectedLabels,
  onEnable,
}: EnableLabelsButtonProps) {
  const { enable: action } = useLabelToolbarActions(selectedLabels, {});
  if (!action.visible) {
    return null;
  }

  return (
    <SharedIconButton
      variant="check"
      onClick={onEnable}
      disabled={action.disabled}
      tooltipLabel={action.hint ?? action.label}
    />
  );
}

type LabelManagerGridToolbarProps = DeleteLabelsButtonProps &
  DisableLabelsButtonProps &
  EnableLabelsButtonProps;

export default function LabelManagerGridToolbar({
  selectedLabels,
  hasActiveLockByLabelId,
  onDelete,
  isDeleting,
  onDisable,
  onEnable,
}: LabelManagerGridToolbarProps) {
  const queryClient = useQueryClient();
  const { features } = useUserSettings();

  return (
    <SharedToolbar
      primaryButtons={
        isFeatureEnabled('driveLabelsQ4', features) ? (
          <CreateLabelButton />
        ) : undefined
      }
      secondaryButtons={
        <>
          {isFeatureEnabled('driveLabelsQ4', features) && (
            <>
              <DisableLabelsButton
                selectedLabels={selectedLabels}
                hasActiveLockByLabelId={hasActiveLockByLabelId}
                onDisable={onDisable}
              />
              <EnableLabelsButton
                selectedLabels={selectedLabels}
                onEnable={onEnable}
              />
              <DeleteLabelsButton
                selectedLabels={selectedLabels}
                onDelete={onDelete}
                isDeleting={isDeleting}
              />
            </>
          )}
          <SharedIconButton
            variant="refresh"
            onClick={() => {
              queryClient.invalidateQueries({
                queryKey: getAllDriveLabelsForManagerQuery().queryKey,
              });
            }}
            tooltipLabel="Refresh"
          />
          <SharedGridStateIconButton
            gridStateKey="labelManagerGridStates"
            module={ModuleList.modules.userManagement.name}
            section={ModuleList.modules.userManagement.sections.labels}
          />
        </>
      }
    />
  );
}
